import { reportException, SkywardException } from "#skyward_error";
import { showErrorDialog } from "#skyward_modalLayer";
import { AjaxManager, AjaxManagerRequestSettings, AjaxSettings } from "../../skyward.base.ajaxManager";
import { Dictionary, Global } from "../../types";
import * as MainMenuData from "./index";

export interface CachedPortalData
{
    selectedModuleID: number | null;
    moduleButtons: MainMenuData.ModuleButtonData[];
    modules: Dictionary<number, MainMenuData.ModuleMenuScreenCategoriesData>;
}

export interface MainMenuCache
{
    /** The HTML element that the menu data originated from
     * Currently only used in the Mobile Main Menu */
    menuLocation: HTMLElement | null;
    selectedPortalCode: string;
    portalButtons: MainMenuData.PortalButtonData[];
    selectedModuleID: number | null;

    portals: Dictionary<string, CachedPortalData>;

    readonly selectedModuleButtonDatas: MainMenuData.ModuleButtonData[];

    initializeOnDomLoad(dataSource: HTMLElement, menuData: MainMenuData.MenuData): void;

    getPortal(portalCode: string): JQueryPromise<CachedPortalData>;
    getModuleCategories(portalCode: string, moduleID: number): JQueryPromise<MainMenuData.ModuleMenuScreenCategoryData[]>;
}

export function createCache($: JQueryStatic, globals: Global, ajaxManager: AjaxManager): MainMenuCache
{
    let menuLocation: HTMLElement | null = null;
    let portalButtons: MainMenuData.PortalButtonData[] = [];
    let selectedPortalCode: string = "";
    let portals: Dictionary<string, CachedPortalData> = {};

    const cache: MainMenuCache =
    {
        get menuLocation() { return menuLocation; },
        set menuLocation(location) { menuLocation = location; },

        get selectedPortalCode() { return selectedPortalCode; },
        set selectedPortalCode(code) { selectedPortalCode = code; },

        get portalButtons() { return portalButtons; },
        set portalButtons(buttons) { portalButtons = buttons; },

        get selectedModuleID() { return portals[selectedPortalCode]?.selectedModuleID || null; },
        set selectedModuleID(moduleID)
        {
            const portal = portals[selectedPortalCode];
            if (portal !== undefined)
            {
                portal.selectedModuleID = moduleID;
            }
        },

        get portals() { return portals; },
        set portals(portalList) { portals = portalList; },

        get selectedModuleButtonDatas() { return portals[selectedPortalCode]?.moduleButtons || []; },

        initializeOnDomLoad: initializeOnDomLoad,

        getPortal: getPortal,
        getModuleCategories: getModuleCategories
    };

    return cache;

    function initializeOnDomLoad(dataSource: HTMLElement, menuData: MainMenuData.MenuData): void
    {
        menuLocation = dataSource;
        portalButtons = menuData.portalBar.buttons;

        const moduleID = menuData.portalContent.selectedModuleID;
        const portalCode = menuData.portalCode;

        if (moduleID === null || portalCode === null)
        {
            return;
        }

        selectedPortalCode = portalCode;

        const buttons = menuData.portalContent.moduleMenu ? menuData.portalContent.moduleMenu.buttons : [];
        const categories = menuData.portalContent.screenCategories ? menuData.portalContent.screenCategories.categories : [];

        addPortal(portalCode, buttons, categories, moduleID);
    }

    function addPortal(portalCode: string, moduleButtonDatas: MainMenuData.ModuleButtonData[], selectedModuleCategories: MainMenuData.ModuleMenuScreenCategoryData[], selectedModuleID: number): void
    {
        portals[portalCode] = createPortal(moduleButtonDatas, selectedModuleCategories, selectedModuleID);
    }

    function createPortal(moduleButtonDatas: MainMenuData.ModuleButtonData[], selectedModuleCategories: MainMenuData.ModuleMenuScreenCategoryData[], selectedModuleID: number): CachedPortalData
    {
        const newModule: MainMenuData.ModuleMenuScreenCategoriesData = { categories: selectedModuleCategories };

        const newModules: Dictionary<number, MainMenuData.ModuleMenuScreenCategoriesData> = Object.create(null); // Avoid default object properties for dictionaries
        newModules[selectedModuleID] = newModule;

        const newPortal: CachedPortalData =
        {
            selectedModuleID: selectedModuleID,
            moduleButtons: moduleButtonDatas,
            modules: newModules
        };

        return newPortal;
    }

    function getPortal(portalCode: string): JQueryPromise<CachedPortalData>
    {
        const portal = portals[portalCode];
        if (portal !== undefined)
        {
            return $.Deferred<CachedPortalData>().resolve(portal).promise();
        }

        const data = { portalCode: portalCode };
        return fetch<MainMenuData.PortalData>("getPortalContent", "SkySys/MenuModule/GetPortalContent", data).then(function (portalData)
        {
            if (portalData === null || portalData.selectedModuleID === null)
            {
                return $.Deferred<CachedPortalData>().reject();
            }

            const portal = createPortal(portalData.moduleMenu.buttons, portalData.screenCategories.categories, portalData.selectedModuleID);
            portals[portalCode] = portal;

            return portal;
        });
    }

    function getModuleCategories(portalCode: string, moduleID: number): JQueryPromise<MainMenuData.ModuleMenuScreenCategoryData[]>
    {
        if (!portals || portalCode === undefined || !portals[portalCode])
        {
            const possiblePortals = portals ? Object.keys(portals).join(",") : "null";

            reportException(new SkywardException(`Unknown portal. Possible PortalCodes: ${possiblePortals}. PortalCode: ${portalCode}. Menu Module ID: ${moduleID}.`));
            showErrorDialog("There was an error retrieving the menu modules.", "Please try refreshing your browser.", {});
            return $.Deferred<MainMenuData.ModuleMenuScreenCategoryData[]>().reject().promise();
        }

        const moduleCategories = portals[portalCode]?.modules[moduleID]?.categories;
        if (moduleCategories !== undefined)
        {
            return $.Deferred<MainMenuData.ModuleMenuScreenCategoryData[]>().resolve(moduleCategories).promise();
        }

        const data = { moduleID: moduleID, portalCode: portalCode };
        return fetch<MainMenuData.ModuleMenuScreenCategoriesData>("getMenusForModule", "SkySys/MenuModule/GetMenusForModule", data).then(function (categoryData)
        {
            const modules = portals[portalCode]?.modules;
            if (modules === undefined)
            {
                return $.Deferred<MainMenuData.ModuleMenuScreenCategoryData[]>().reject();
            }

            modules[moduleID] = categoryData;

            return categoryData.categories;
        });
    }

    function fetch<T>(requestID: string, path: string, data: any): JQuery.jqXHR<T>
    {
        const ajaxSettings: AjaxSettings =
        {
            url: globals.virtualDir + path,
            type: "POST",
            dataType: "JSON",
            activityIndicator: { hideGlass: true },
            data: data
        };

        const managerSettings: AjaxManagerRequestSettings =
        {
            requestID: requestID
        };

        return ajaxManager.ajax(ajaxSettings, managerSettings);
    }
};